import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CatalystEvent } from '@catalyst-iq/shared';
import { api } from '../services/api';
import { TickerIcon } from '../components/TickerIcon';
import { formatDayOnly, formatTimeOnly } from '../components/event/dateFormatter';

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState<CatalystEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!id) return;

    async function fetchEvent() {
      const eventId = decodeURIComponent(id!);
      try {
        setLoading(true);
        setError(null);
        const data = await api.getEvent(eventId);
        setEvent(data.event);
      } catch (err: any) {
        if (err?.response?.status === 404) {
          setError('not_found');
        } else {
          setError(err?.message || 'Failed to load article');
        }
      } finally {
        setLoading(false);
      }
    }

    void fetchEvent();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center gap-3">
        <div className="w-6 h-6 border-2 border-gray-200 dark:border-cat-border-2 border-t-amber-500 rounded-full animate-spin" />
        <div className="text-[13px] text-gray-400 dark:text-cat-text-muted">
          Loading article…
        </div>
      </div>
    );
  }

  if (error === 'not_found' || !event) {
    return (
      <div className="max-w-3xl mx-auto py-16 px-6 text-center">
        <h1 className="text-xl font-bold text-gray-900 dark:text-[#f1f5fb] mb-3">Article not found</h1>
        <p className="text-gray-500 dark:text-[#c2d0e0] text-sm mb-6">
          This event may have been removed or the link is invalid.
        </p>
        <Link
          to="/events"
          className="inline-flex items-center gap-1.5 text-cat-accent hover:opacity-80 text-sm font-medium"
        >
          ← Back to Event Feed
        </Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto py-16 px-6 text-center">
        <h1 className="text-xl font-bold text-red-500 mb-3">Failed to load article</h1>
        <p className="text-gray-500 dark:text-[#c2d0e0] text-sm mb-6">{error}</p>
        <Link
          to="/events"
          className="inline-flex items-center gap-1.5 text-cat-accent hover:opacity-80 text-sm font-medium"
        >
          ← Back to Event Feed
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Link
        to="/events"
        className="inline-flex items-center gap-1.5 text-[12px] font-medium text-gray-500 dark:text-[#c2d0e0] hover:text-cat-accent transition-colors"
      >
        ← Back to Event Feed
      </Link>

      <article className="rounded-2xl border border-gray-200 dark:border-[#1d2d47] bg-white dark:bg-[#0e1624] overflow-hidden shadow-sm">
        <div className="h-[3px] bg-gradient-to-r from-[#f5a623] via-[#f97316] to-[#f5476a]" />

        <div className="p-6 sm:p-8">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            {event.ticker && <TickerIcon ticker={event.ticker} size="sm" />}
            {event.ticker && (
              <span className="text-sm font-bold text-gray-900 dark:text-[#f1f5fb] uppercase">
                {event.ticker}
              </span>
            )}
            <span className="text-[10px] text-gray-400 dark:text-[#c2d0e0] uppercase tracking-widest font-mono">
              {formatDayOnly(event.ts)} · {formatTimeOnly(event.ts)}
            </span>
            {event.is_negative && (
              <span className="flex items-center gap-1.5 text-[9px] font-bold tracking-widest uppercase text-red-400 bg-red-900/20 border border-red-800/40 px-2 py-0.5 rounded">
                Negative
              </span>
            )}
          </div>

          <h1 className="text-[22px] sm:text-[26px] font-semibold text-gray-900 dark:text-[#f1f5fb] leading-snug mb-5">
            {event.title}
          </h1>

          {(event as any).content ? (
            <div className="space-y-4">
              <div className="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-[#c2d0e0]">
                {(event as any).contentStatus === 'full' ? 'Full article content' : 'Article preview'}
              </div>
              <div className="text-[15px] text-gray-700 dark:text-[#d5deea] leading-relaxed whitespace-pre-wrap">
                {(event as any).content}
              </div>
            </div>
          ) : event.summary ? (
            <div className="text-[15px] text-gray-700 dark:text-[#d5deea] leading-relaxed whitespace-pre-wrap">
              {event.summary}
            </div>
          ) : (
            <p className="text-[14px] text-gray-500 dark:text-[#c2d0e0] italic">
              No summary is available for this article.
            </p>
          )}

          <footer className="mt-10 pt-5 border-t border-gray-100 dark:border-[#1d2d47]">
            <p className="text-[11px] text-gray-400 dark:text-[#a3b6cc] leading-relaxed">
              Source:{' '}
              {event.link ? (
                <a
                  href={event.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cat-accent hover:opacity-80 underline-offset-2 hover:underline"
                >
                  {event.source}
                </a>
              ) : (
                <span>{event.source}</span>
              )}
              {event.link && (
                <span className="text-gray-400 dark:text-[#a3b6cc]">
                  {' '}
                  ·{' '}
                  <a
                    href={event.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cat-accent hover:opacity-80 underline-offset-2 hover:underline"
                  >
                    View original
                  </a>
                </span>
              )}
            </p>
          </footer>
        </div>
      </article>
    </div>
  );
}
